import { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { categories } from '@/mocks/categories';

const articleLinks = [
  { slug: 'kakobuy-spreadsheet-complete-guide', label: 'Complete Guide', icon: 'ri-book-open-line' },
  { slug: 'kakobuy-spreadsheet-vs-cssbuy-superbuy', label: 'Platform Comparison', icon: 'ri-scales-3-line' },
  { slug: 'kakobuy-spreadsheet-for-beginners', label: 'Beginner Guide', icon: 'ri-seedling-line' },
  { slug: 'best-kakobuy-spreadsheet-finds-2026', label: 'Best Finds 2026', icon: 'ri-star-line' },
];

export default function Navbar() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [mobileCatExpanded, setMobileCatExpanded] = useState(false);
  const [mobileGuidesExpanded, setMobileGuidesExpanded] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 60);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setIsDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Lock body scroll when mobile menu is open
  useEffect(() => {
    if (isMobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [isMobileMenuOpen]);

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled
          ? 'bg-background-50/95 backdrop-blur-md border-b border-background-200/60'
          : 'bg-transparent'
      }`}
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-10">
        <div className="flex items-center justify-between h-16 md:h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2 shrink-0">
            <span className="font-heading text-xl md:text-2xl font-bold text-foreground-900 tracking-tight">
              Kakobuy<span className="text-primary-500">Finds</span>
            </span>
          </Link>

          {/* Desktop Nav */}
          <div className="hidden md:flex items-center gap-8">
            <Link to="/" className="text-sm font-medium text-foreground-700 hover:text-foreground-950 transition-colors whitespace-nowrap">
              Home
            </Link>

            {/* Category Dropdown */}
            <div className="relative" ref={dropdownRef}>
              <button
                onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                className="flex items-center gap-1.5 text-sm font-medium text-foreground-700 hover:text-foreground-950 transition-colors whitespace-nowrap cursor-pointer"
              >
                Categories
                <i className={`ri-arrow-down-s-line text-sm transition-transform duration-200 ${isDropdownOpen ? 'rotate-180' : ''}`}></i>
              </button>

              {isDropdownOpen && (
                <div className="absolute top-full left-1/2 -translate-x-1/2 mt-3 w-[520px] bg-background-50 rounded-2xl border border-background-200/70 overflow-hidden animate-fade-in-up">
                  <div className="grid grid-cols-2 gap-1 p-3 max-h-[420px] overflow-y-auto">
                    {categories.map((cat) => (
                      <a
                        key={cat.id}
                        href={cat.url}
                        target="_blank"
                        rel="noopener nofollow"
                        className="flex items-center gap-3 px-3 py-2.5 rounded-xl hover:bg-background-100 transition-colors group"
                      >
                        <div className="w-9 h-9 flex items-center justify-center rounded-lg bg-background-100 group-hover:bg-primary-50 transition-colors shrink-0">
                          <i className={`${cat.icon} text-base text-foreground-600 group-hover:text-primary-500 transition-colors`}></i>
                        </div>
                        <span className="text-sm font-medium text-foreground-800 group-hover:text-foreground-950 transition-colors whitespace-nowrap">
                          {cat.name}
                        </span>
                        <span className="ml-auto text-xs text-foreground-400">{cat.count} finds</span>
                      </a>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <Link to="/article/kakobuy-spreadsheet-complete-guide" className="text-sm font-medium text-foreground-700 hover:text-foreground-950 transition-colors whitespace-nowrap">
              Guides
            </Link>
            <Link to="/article/best-kakobuy-spreadsheet-finds-2026" className="text-sm font-medium text-foreground-700 hover:text-foreground-950 transition-colors whitespace-nowrap">
              Blog
            </Link>
          </div>

          {/* CTA Buttons */}
          <div className="hidden md:flex items-center gap-3">
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-semibold text-background-50 bg-primary-500 hover:bg-primary-600 rounded-full transition-colors whitespace-nowrap cursor-pointer"
            >
              Kakobuy Sheet
              <i className="ri-arrow-right-line text-sm"></i>
            </a>
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-semibold text-primary-600 border-2 border-primary-500 hover:bg-primary-50 rounded-full transition-colors whitespace-nowrap cursor-pointer"
            >
              Buy It Now
              <i className="ri-arrow-right-line text-sm"></i>
            </a>
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="md:hidden w-10 h-10 flex items-center justify-center rounded-xl text-foreground-700 hover:bg-background-100 transition-colors cursor-pointer"
            aria-label={isMobileMenuOpen ? 'Close menu' : 'Open menu'}
          >
            <i className={`text-xl ${isMobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'}`}></i>
          </button>
        </div>

        {/* ====================== MOBILE MENU ====================== */}
        {isMobileMenuOpen && (
          <div className="md:hidden fixed inset-0 top-16 bg-background-50 z-40 overflow-y-auto animate-fade-in-up">
            <div className="px-6 py-6 pb-32">

              {/* Search-type header */}
              <div className="mb-6">
                <div className="relative">
                  <i className="ri-search-line absolute left-4 top-1/2 -translate-y-1/2 text-foreground-400 text-base"></i>
                  <input
                    type="text"
                    placeholder="Search categories or guides..."
                    className="w-full pl-11 pr-4 py-3 text-sm bg-background-100 border border-background-200/70 rounded-2xl text-foreground-800 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300 transition-colors"
                  />
                </div>
              </div>

              {/* Quick CTA Buttons */}
              <div className="flex gap-3 mb-8">
                <a
                  href="https://kaspreadsheet.com"
                  target="_blank"
                  rel="noopener nofollow"
                  className="flex-1 flex items-center justify-center gap-2 py-3 text-sm font-semibold text-background-50 bg-primary-500 hover:bg-primary-600 rounded-full transition-colors whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-shopping-bag-3-line"></i>
                  Kakobuy Sheet
                </a>
                <a
                  href="https://kaspreadsheet.com"
                  target="_blank"
                  rel="noopener nofollow"
                  className="flex-1 flex items-center justify-center gap-2 py-3 text-sm font-semibold text-primary-600 border-2 border-primary-400 hover:bg-primary-50 rounded-full transition-colors whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-flashlight-line"></i>
                  Buy It Now
                </a>
              </div>

              {/* Home Link */}
              <Link
                to="/"
                onClick={() => setIsMobileMenuOpen(false)}
                className="flex items-center gap-3 px-3 py-3 rounded-xl hover:bg-background-100 transition-colors mb-1"
              >
                <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-background-100 text-foreground-600">
                  <i className="ri-home-4-line text-lg"></i>
                </div>
                <span className="text-sm font-semibold text-foreground-800">Home</span>
              </Link>

              {/* Categories Section - Expandable */}
              <div className="mt-2">
                <button
                  onClick={() => setMobileCatExpanded(!mobileCatExpanded)}
                  className="w-full flex items-center justify-between px-3 py-3 rounded-xl hover:bg-background-100 transition-colors cursor-pointer"
                >
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-accent-50 text-accent-600">
                      <i className="ri-grid-line text-lg"></i>
                    </div>
                    <span className="text-sm font-semibold text-foreground-800">Categories</span>
                    <span className="text-xs text-foreground-400 bg-background-100 px-2 py-0.5 rounded-full">14</span>
                  </div>
                  <i className={`ri-arrow-down-s-line text-foreground-400 text-lg transition-transform duration-200 ${mobileCatExpanded ? 'rotate-180' : ''}`}></i>
                </button>

                {mobileCatExpanded && (
                  <div className="mt-1 ml-2 pl-4 border-l-2 border-background-200/60">
                    <div className="flex flex-col gap-0.5 max-h-[420px] overflow-y-auto py-1">
                      {categories.map((cat) => (
                        <a
                          key={cat.id}
                          href={cat.url}
                          target="_blank"
                          rel="noopener nofollow"
                          className="flex items-center gap-3 px-3 py-2.5 rounded-xl hover:bg-background-100 transition-colors group"
                        >
                          <div className="w-9 h-9 flex items-center justify-center rounded-lg bg-background-100 group-hover:bg-primary-50 transition-colors shrink-0">
                            <i className={`${cat.icon} text-base text-foreground-500 group-hover:text-primary-500 transition-colors`}></i>
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="text-sm font-medium text-foreground-700 group-hover:text-foreground-950 transition-colors">
                              {cat.name}
                            </div>
                          </div>
                          <span className="text-xs text-foreground-400 bg-background-100 group-hover:bg-primary-50 px-2 py-0.5 rounded-full transition-colors">
                            {cat.count}
                          </span>
                        </a>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* Guides Section - Expandable */}
              <div className="mt-1">
                <button
                  onClick={() => setMobileGuidesExpanded(!mobileGuidesExpanded)}
                  className="w-full flex items-center justify-between px-3 py-3 rounded-xl hover:bg-background-100 transition-colors cursor-pointer"
                >
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-secondary-50 text-secondary-600">
                      <i className="ri-article-line text-lg"></i>
                    </div>
                    <span className="text-sm font-semibold text-foreground-800">Guides &amp; Articles</span>
                    <span className="text-xs text-foreground-400 bg-background-100 px-2 py-0.5 rounded-full">4</span>
                  </div>
                  <i className={`ri-arrow-down-s-line text-foreground-400 text-lg transition-transform duration-200 ${mobileGuidesExpanded ? 'rotate-180' : ''}`}></i>
                </button>

                {mobileGuidesExpanded && (
                  <div className="mt-1 ml-2 pl-4 border-l-2 border-background-200/60">
                    <div className="flex flex-col gap-0.5 py-1">
                      {articleLinks.map((article) => (
                        <Link
                          key={article.slug}
                          to={`/article/${article.slug}`}
                          onClick={() => setIsMobileMenuOpen(false)}
                          className="flex items-center gap-3 px-3 py-2.5 rounded-xl hover:bg-background-100 transition-colors group"
                        >
                          <div className="w-9 h-9 flex items-center justify-center rounded-lg bg-background-100 group-hover:bg-secondary-50 transition-colors shrink-0">
                            <i className={`${article.icon} text-base text-foreground-500 group-hover:text-secondary-600 transition-colors`}></i>
                          </div>
                          <span className="text-sm font-medium text-foreground-700 group-hover:text-foreground-950 transition-colors">
                            {article.label}
                          </span>
                        </Link>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* Divider */}
              <div className="my-6 border-t border-background-200/60"></div>

              {/* Stats */}
              <div className="grid grid-cols-3 gap-3 px-1">
                <div className="text-center p-3 rounded-xl bg-background-100">
                  <div className="text-lg font-bold text-foreground-900 font-heading">14</div>
                  <div className="text-xs text-foreground-400 mt-0.5">Categories</div>
                </div>
                <div className="text-center p-3 rounded-xl bg-background-100">
                  <div className="text-lg font-bold text-foreground-900 font-heading">3K+</div>
                  <div className="text-xs text-foreground-400 mt-0.5">Finds</div>
                </div>
                <div className="text-center p-3 rounded-xl bg-background-100">
                  <div className="text-lg font-bold text-foreground-900 font-heading">Weekly</div>
                  <div className="text-xs text-foreground-400 mt-0.5">Updates</div>
                </div>
              </div>

              {/* Trust badge */}
              <div className="mt-6 flex items-center justify-center gap-2 text-xs text-foreground-400">
                <i className="ri-shield-check-line text-accent-500"></i>
                <span>Updated product database &bull; Global shopping resources</span>
              </div>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
}